import { Instance, types } from 'mobx-state-tree';
import { ISupplierItem, ITemplate } from '../../main/entities';
import { mg } from '../../mg/mg';
import { commSvc } from '../../services/CommSvc';
import { toasterSvc } from '../../services/ToasterSvc';
import { getOmnistore } from '../../stores/Omnistore';
import { adOrderingStore, IAdOrderingItem } from './AdsOrderingStore';

const Availability = types.enumeration(['INFRARED', 'BLUE', 'RED', 'YELLOW', 'GREEN']);

interface IdAndName {
	id: number
	name: string
}

interface ITemplateInfo {
	supplierItem?: ISupplierItem
	supplierItemId: number
	templateItemId: number | undefined
	templateId: number
	branchId: number
	supplierName: string
	branchName: string
	availability: Instance<typeof Availability>
	isAllowed: boolean
}

interface IBranchInfo {
	branchId: number
	branchName: string
	isAllowed: boolean
}

export const AdsPageStore = types
	.model('AdsPage', {
		advertisementId: 0,
		itemId: 0,
		branchId: 0,
		templateId: 0,
		amountToOrder: 0,
	})
	.views(self => ({
		get multirepo() {
			return getOmnistore().multirepo;
		}
	}))
	.views(self => ({
		get _adOrderingStore() {
			return adOrderingStore;
		},
	}))
	.actions(self => ({
		init() {
			self.multirepo.advertisementRepo.fetch('INVALIDATE');
		},
		setItemId(itemId: number) {
			self.itemId = itemId;
		},
	}))
	.views(self => ({
		get advertisement() {
			return self.multirepo.advertisementRepo.byId(self.advertisementId);
		},
	}))
	.views(self => ({
		get templates() : Array<ITemplate> {
			return self.multirepo.templateRepo.allAsList.filter(t => t.branchId===self.branchId);
		},
		get template() : ITemplate | undefined {
			return self.multirepo.templateRepo.byId(self.templateId);
		},
		get variations() : Array<IdAndName> {
			const a = self.advertisement;
			if (!a) return [];
			const variations = a.variations.slice().sort(mg.comparatorOn((v: {name: string}) => v.name));
			if (variations.length === 1) window.setTimeout(() => self.setItemId(variations[0].id));
			return variations;
		},
		get isDisabled() : boolean {
			return !self.advertisementId || !self.itemId || !self.branchId || !self.templateId || self.amountToOrder<=0;
		},
	}))
	.views(self => ({
		get _itemsInCatalogs() : Map<number, ISupplierItem> {
			const entries: Array<[number, ISupplierItem]> = getOmnistore().entityManagerStore.get('AdvertiserOfferInCatalogs:' + self.itemId).allAsList
				.map((si: any) => [si.supplierId, si]); //TODO any
			return new Map(entries);
		},
		get _itemsInTemplates() : Map<number, ISupplierItem> {
			const itemFilter = (ti: any) => ti.supplierItemId==self.itemId; //TODO ITemplateItem
			const entries: Array<[number, ISupplierItem]> = self.templates
				.flatMap(t => getOmnistore().entityManagerStore.get('TemplateItem:' + t.id).allAsList.filter(itemFilter))
				.map((ti: any) => [ti.templateId, ti]); //TODO any
			return new Map(entries);
		},
	}))
	.views(self => ({
		templateInfo(template: ITemplate) : ITemplateInfo {
			const t = template;
			const supplierItem: ISupplierItem | undefined = self._itemsInCatalogs.get(t.supplierId);
			const inCatalog = !!supplierItem;
			const supplierItemId: number = supplierItem ? supplierItem.id : self.itemId;
			const templateItemId: number | undefined = getOmnistore().entityManagerStore.get('TemplateItem:' + t.id).allAsList
				.filter((ti: any) => ti.supplierItemId==supplierItemId) //TODO 2x any
				.map((ti: any) => ti.id)[0];
			const inTemplate = !!self._itemsInTemplates.get(t.id);
			const supplier = self.multirepo.supplierRepo.byId(t.supplierId);
			const branch = self.multirepo.branchRepo.byId(t.branchId);
			const a = self.advertisement;
			const forbidden = a && a.templateIds.indexOf(t.id) < 0;
			const availability = forbidden ? 'INFRARED'
				: inTemplate ? 'BLUE'
					: inCatalog ? 'GREEN'
						: supplier && supplier.nonlistedItemsOrderable ? 'YELLOW'
							: 'RED';
			const isAllowed = ['BLUE', 'GREEN', 'YELLOW'].includes(availability);
			return {
				templateId: t.id,
				templateItemId,
				supplierItem,
				supplierItemId,
				supplierName: !supplier ? '?' : supplier.name,
				branchId: t.branchId,
				branchName: !branch ? '?' : branch.name,
				availability,
				isAllowed,
			};
		},
	}))
	.views(self => ({
		get templatesInfos() : Array<ITemplateInfo> {
			return self.multirepo.templateRepo.allAsList.map(t => self.templateInfo(t));
		},
	}))
	.views(self => ({
		get branchInfos() : Array<IBranchInfo> {
			const result = new Map<number, IBranchInfo>();
			self.templatesInfos.forEach(t => {
				if (!t.isAllowed && result.has(t.branchId)) return;
				result.set(t.branchId, {branchId: t.branchId, branchName: t.branchName, isAllowed: t.isAllowed});
			});
			return [...result.values()].sort(mg.comparatorOn(b => b.branchName));
		},
		get filteredTemplatesInfos() : Array<ITemplateInfo> {
			return self.templatesInfos.filter(t => t.branchId===self.branchId);
		},
		get allAdvertisements() {
			return self.multirepo.advertisementRepo.allAsList;
		},
	}))
	.actions(self => ({
		reset() {
			self.advertisementId = 0;
			self.itemId = 0;
			self.branchId = getOmnistore().locationStore.branchId;
			self.templateId = 0;
			self.amountToOrder = 0;
		},
	}))
	.actions(self => ({
		setAdvertisementId(advertisementId: number) {
			if (self.advertisementId === advertisementId) return;
			self.reset();
			self.advertisementId = advertisementId;
			if (!advertisementId) return;
			commSvc.doPost('RecordAdClick', {offerId: advertisementId}).then(toasterSvc.failure);
			const a = self.advertisement;
			if (!mg.assertPresent(a)) return;
			self.amountToOrder = a.amountStep;
		},
		setBranchId(branchId: number) {
			self.branchId = branchId;
			if (!self.templates.filter(t => t.id===self.templateId).length) self.templateId = 0;
		},
		setTemplateId(templateId: number) {
			self.templateId = templateId;
		},
		setAmountToOrder(amountToOrder: number) {
			self.amountToOrder = amountToOrder>0 ? amountToOrder : 0;
		},
		_acceptViaTemplate(templateInfo: ITemplateInfo) {
			let {templateItemId, templateId, supplierItemId} = templateInfo;
			const amountToOrder = self.amountToOrder;
			let promise : Promise<unknown>	= Promise.resolve();
			if (!templateInfo.templateItemId) {
				promise = commSvc.doPost('CreateTemplateItem', {templateId, supplierItemId, amountToOrder})
					.then(toasterSvc.failure)
					.then(commSvc.requireOutputObject)
					.then(r => templateItemId = r.outputObject.id);
			}
			promise.then(() => {
				const syncingCode = getOmnistore().entityManagerStore.get('TemplateItem:' + templateId).etag;
				return commSvc.doPost('UpdateTemplateItems', {templateId, syncingCode, items: [{id: templateItemId, amountToOrder}]});
			})
				.then(toasterSvc.failure)
				.then(() => getOmnistore().entityManagerStore.get('TemplateItem', templateId).fetch())
				.finally(self.reset);
		},
		_acceptAvoidingTemplate(templateInfo: ITemplateInfo) {
			const {templateId, supplierItem, supplierItemId, supplierName} = templateInfo;
			if (!mg.assertPresent(supplierItem, 'no supplierItem')) return;
			const {advertisementId, amountToOrder} = self;
			const {name, imageLink} = self.advertisement!;
			const item: IAdOrderingItem = {advertisementId, templateId, supplierItem, supplierItemId, supplierName, amountToOrder, name, imageLink};
			self._adOrderingStore.add(item);
			self.reset();
		},
	}))
	.actions(self => ({
		accept() {
			const a = self.advertisement;
			if (!mg.checkPresent(a, 'missing advertisement')) return;
			const template = self.template;
			if (!mg.checkPresent(template)) return;
			const templateInfo = self.templateInfo(template);
			console.log('templateInfo', mg.stringify(templateInfo));
			if (a.viaTemplate) {
				self._acceptViaTemplate(templateInfo);
			} else {
				self._acceptAvoidingTemplate(templateInfo);
			}
		},
	}));

export const specialOffersPageStore = AdsPageStore.create({});
